import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import toast from 'react-hot-toast'
import { PlusIcon, PencilIcon, TrashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { useAuthStore } from '@/store/authStore'

interface Admin {
  _id: string
  email: string
  role: 'admin' | 'superadmin' | 'moderator'
  lastLoginAt?: string
  createdAt: string
}

export default function AdminsPage() {
  const queryClient = useQueryClient()
  const currentAdmin = useAuthStore((state) => state.admin)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null)
  
  const { data: admins, isLoading } = useQuery({
    queryKey: ['admins'],
    queryFn: async () => {
      const res = await api.get<{ data: { admins: Admin[] } }>('/admin/admins')
      return res.data.data.admins
    },
  })
  
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      await api.post('/admin/admins', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] })
      toast.success('Администратор создан')
      setIsModalOpen(false)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Ошибка создания')
    },
  })
  
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await api.patch(`/admin/admins/${id}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] })
      toast.success('Администратор обновлён')
      setEditingAdmin(null)
      setIsModalOpen(false)
    },
  })
  
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/admins/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] })
      toast.success('Администратор удалён')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Ошибка удаления')
    },
  })
  
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      email: formData.get('email') as string,
      password: formData.get('password') as string,
      role: formData.get('role') as string,
    }
    
    if (editingAdmin) {
      const updateData: any = { role: data.role }
      if (data.email !== editingAdmin.email) {
        updateData.email = data.email
      }
      if (data.password) {
        updateData.password = data.password
      }
      updateMutation.mutate({ id: editingAdmin._id, data: updateData })
    } else {
      createMutation.mutate(data)
    }
  }
  
  const isSuperAdmin = currentAdmin?.role === 'superadmin'
  
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Администраторы</h1>
        {isSuperAdmin && (
          <button
            onClick={() => {
              setEditingAdmin(null)
              setIsModalOpen(true)
            }}
            className="btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Добавить
          </button>
        )}
      </div>
      
      {!isSuperAdmin && (
        <div className="card bg-yellow-500/10 border-yellow-500/30">
          <p className="text-yellow-400">
            Только супер-администраторы могут управлять учётными записями админов
          </p>
        </div>
      )}
      
      <div className="card">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3 px-4 font-medium text-gray-400">Email</th>
              <th className="text-left py-3 px-4 font-medium text-gray-400">Роль</th>
              <th className="text-left py-3 px-4 font-medium text-gray-400">Последний вход</th>
              <th className="text-left py-3 px-4 font-medium text-gray-400">Создан</th>
              {isSuperAdmin && (
                <th className="text-right py-3 px-4 font-medium text-gray-400">Действия</th>
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="text-center py-8 text-gray-400">
                  Загрузка...
                </td>
              </tr>
            ) : admins?.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-8 text-gray-400">
                  Администраторы не найдены
                </td>
              </tr>
            ) : (
              admins?.map((admin) => (
                <tr key={admin._id} className="border-b border-gray-700/50 hover:bg-gray-700/20">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white">{admin.email}</span>
                      {admin._id === currentAdmin?._id && (
                        <span className="badge-blue">Вы</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {admin.role === 'superadmin' && (
                        <ShieldCheckIcon className="w-4 h-4 text-yellow-400" />
                      )}
                      <span className={admin.role === 'superadmin' ? 'text-yellow-400' : 'text-gray-300'}>
                        {admin.role === 'superadmin' ? 'Супер-админ' : admin.role === 'moderator' ? 'Модератор' : 'Админ'}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-400">
                    {admin.lastLoginAt ? formatDate(admin.lastLoginAt) : 'Никогда'}
                  </td>
                  <td className="py-3 px-4 text-gray-400">
                    {formatDate(admin.createdAt)}
                  </td>
                  {isSuperAdmin && (
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setEditingAdmin(admin)
                            setIsModalOpen(true)
                          }}
                          className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                          title="Редактировать"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        {admin._id !== currentAdmin?._id && (
                          <button
                            onClick={() => {
                              if (confirm('Удалить администратора?')) {
                                deleteMutation.mutate(admin._id)
                              }
                            }}
                            className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                            title="Удалить"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Modal */}
      {isModalOpen && isSuperAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">
                {editingAdmin ? 'Редактировать' : 'Новый администратор'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  defaultValue={editingAdmin?.email}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Пароль {editingAdmin ? '(оставьте пустым, чтобы не менять)' : '*'}
                </label>
                <input
                  type="password"
                  name="password"
                  required={!editingAdmin}
                  minLength={8}
                  className="w-full"
                  placeholder="Минимум 8 символов"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Роль *
                </label>
                <select
                  name="role"
                  required
                  defaultValue={editingAdmin?.role || 'admin'}
                  className="w-full"
                >
                  <option value="admin">Администратор</option>
                  <option value="moderator">Модератор</option>
                  <option value="superadmin">Супер-администратор</option>
                </select>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false)
                    setEditingAdmin(null)
                  }}
                  className="btn-secondary"
                >
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  {editingAdmin ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
